// Shared parser for the worker's structured `SCENE: / PEOPLE: / EVENTS: / NOTES:`
// description blob. Used by EventCard, EventList summary, and DescriptionPanel
// so the four call sites can't drift.
//
// The worker prompt enforces all four labels but tolerates loosely-formatted
// fallbacks (missing label entirely, lowercase, repeated colons, multi-line
// section bodies). We keep the parser permissive so a slightly wrong
// description still renders rather than collapsing.
const KNOWN = ["SCENE", "PEOPLE", "EVENTS", "NOTES"];
const PATTERN = /^(SCENE|PEOPLE|EVENTS|NOTES)\s*:\s*(.*)$/i;
// Machine-read fields the worker prompt appends after the four prose
// sections (FALL: yes/no fall-gate, BEST_FRAME: N alert-snapshot pick).
// A line opening with one must terminate the current section rather than
// being glued on as a continuation; inline occurrences (nova one-line
// scramble) are scrubbed from section bodies. UPPERCASE labels only and
// no newlines inside a token — prose like "risk of fall: no injuries"
// or "prevent a fall:\nNo one was hurt" must survive. The prompt
// mandates literal uppercase labels, so real machine fields always match.
const MACHINE_LINE = /^(?:FALL|BEST[_ ]?FRAME)[ \t]*:/;
const MACHINE_INLINE = /[ \t]*\b(?:FALL[ \t]*:[ \t]*(?:yes|no|Yes|No|YES|NO)|BEST[_ ]?FRAME[ \t]*:[ \t]*\d{1,3})\b\.?/g;
// Strip "frame N" / "frames N-M" / "by frame N" / "in frames N to M" /
// "frames N–M" (en-dash) / "during frame N" — the LLM's keyframe indices
// don't match the operator's playback timeline, so the references read as
// noise. Forward-compatible with the prompt that now forbids these (new
// clips won't generate them); back-compatible cleanup for already-stored
// descriptions until they're re-described.
//
// Strategy: only eat the preposition + "frame(s) N(-M)" cluster. Keep any
// leading verb ("walks", "enters", "visible") intact — those words are
// meaningful on their own and the operator just needs the timing noise
// gone, not the action.
const FRAME_REF_PATTERNS = [
    // Possessive lead-in: "is visible in frames 8-15", "are observed in frame 5"
    // The participle binds the frame ref tightly to the verb. Drop "is visible"
    // along with the frame ref, otherwise the residual "X is visible" reads
    // like an incomplete clause.
    /\s+(?:is|are|was|were)\s+(?:visible|observed|present|seen|shown)\s+(?:in|during|throughout|across|from|by)\s+frames?\s+\d+(?:\s*(?:[-–—]|to|through|thru|and|until)\s*\d+)?\b/gi,
    // Bare parenthetical: "(frames 7-14)", "(in frame 5)"
    /\s*\(\s*(?:in\s+|during\s+|around\s+)?frames?\s+\d+(?:\s*(?:[-–—]|to|through|thru|and|until)\s*\d+)?\s*\)/gi,
    // Prepositional phrase: "in frames 3-12", "during frames 7-9", "by frame 12",
    // "from frames 3 to 7", "around frame 5". Leading whitespace required so
    // we don't eat a sentence-initial "in" elsewhere.
    /[\s,;:]\b(?:in|during|across|over|throughout|from|by|around|approximately|approx\.?|near|until|after|before)\s+frames?\s+\d+(?:\s*(?:[-–—]|to|through|thru|and|until)\s*\d+)?\b/gi,
    // Bare suffix: "...observed, frames 7-14.", "...activity frame 5."
    /[\s,;:]\bframes?\s+\d+(?:\s*(?:[-–—]|to|through|thru|and|until)\s*\d+)?\b/gi,
];
export function stripFrameReferences(text) {
    if (!text)
        return text;
    let out = text;
    for (const re of FRAME_REF_PATTERNS) {
        out = out.replace(re, "");
    }
    // Collapse the whitespace + stray punctuation left behind.
    out = out
        .replace(/\s+([,.;:!?])/g, "$1") // " ," → ","
        .replace(/([,;:])\s*([,.;:])/g, "$2") // ",." → "."
        .replace(/\(\s*\)/g, "")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s,;:.\-—–]+/, "")
        .trim();
    return out;
}
export function parseSections(desc) {
    if (!desc)
        return [];
    const out = [];
    let current = null;
    for (const raw of desc.split(/\r?\n/)) {
        const line = raw.trim();
        const m = line.match(PATTERN);
        if (m) {
            if (current)
                out.push(current);
            current = {
                label: m[1].toUpperCase(),
                text: m[2].trim(),
            };
        }
        else if (MACHINE_LINE.test(line)) {
            // FALL: / BEST_FRAME: are for the worker's regexes, not the operator.
            if (current)
                out.push(current);
            current = null;
        }
        else if (current && line) {
            // Continuation of the current section (multi-line body).
            current.text = current.text ? `${current.text} ${line}` : line;
        }
    }
    if (current)
        out.push(current);
    // Stable order: always emit SCENE → PEOPLE → EVENTS → NOTES even if the
    // upstream prompt drifts, so DescriptionPanel doesn't reflow per row.
    // Frame-number references are stripped from each section body — those
    // index into the keyframe slideshow, not the playback timeline.
    return KNOWN
        .map(label => out.find(s => s.label === label))
        .filter((s) => s !== undefined)
        .map(s => ({
        ...s,
        text: stripFrameReferences(s.text.replace(MACHINE_INLINE, "").trim()),
    }));
}
const NO_SIGNAL = /^(no\b|none\b|nothing\b|empty\b|n\/a\b|—|-)\s*/i;
// EVENTS / PEOPLE: any leading no-signal word filters the whole text
// (worker writes "No people visible.", "Nothing happening." etc).
// SCENE: looser — "empty parking lot at night" starts with "empty" but is
// genuinely descriptive. Only filter SCENE when the text is short enough
// to plausibly be a no-signal stub ("Empty.", "—", "N/A").
export function isSubstantive(text, sceneFallback = false) {
    if (!text)
        return false;
    if (!NO_SIGNAL.test(text))
        return true;
    if (sceneFallback) {
        return text.trim().split(/\s+/).length > 2;
    }
    return false;
}
// One-line summary for compact list rows. EVENTS dominates, then PEOPLE,
// then SCENE — matches operator mental model of "what's happening here?"
// over "where is here?". Returns "" rather than fabricating text so the
// caller can choose a fallback (timestamp, "no description", etc).
export function oneLineSummary(desc) {
    const sections = parseSections(desc);
    const find = (l) => sections.find(s => s.label === l)?.text ?? "";
    const events = find("EVENTS");
    if (isSubstantive(events))
        return events;
    const people = find("PEOPLE");
    if (isSubstantive(people))
        return people;
    const scene = find("SCENE");
    if (isSubstantive(scene, true))
        return scene;
    return "";
}
